import { Grid } from '@material-ui/core';
import _, { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { v4 as uuid } from 'uuid';

import { doPostQCXRequest } from '../../../utils/api/domain/domain-api-utils';
import QCXFinalBondManager from '../../../shared-components/final-gerenciador-relacionamento/QCXFinalBondManager';

import SelectFundamentosOpcionais from '../adicoesForm/components/SelectFundamentosOpcionais';

export default function QCXAuxiliarSelectFundamentosOpcionais({
  name = '',
  values = {},
  disabled = false,
  faturaId,
  onMercadoriasPermitidasChange,
  ...restProps
}) {
  const form = useForm();
  const { values: formValues } = useFormState();
  const [listFundamentos, setListFundamentos] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchFundamentos = async () => {
      try {
        const response = await doPostQCXRequest(['diduimp', 'auxiliar', 'tratamentos-tributarios-opcionais'], {
          mercadoriaIds: [],
        });
        const tratamentos = Array.isArray(response?.data) ? response.data : [response?.data];
        const fundamentosMap = new Map();

        tratamentos.forEach((tratamentoDisponivel) => {
          const tratamento = tratamentoDisponivel?.tratamentos;
          const fundamentoLegal = tratamento?.fundamentoLegal;

          if (!fundamentoLegal?.codigo || !fundamentoLegal?.nome) return;

          const fundamento = fundamentosMap.get(fundamentoLegal.codigo) || {
            id: fundamentoLegal.codigo,
            tipo: fundamentoLegal.tipo,
            nome: fundamentoLegal.nome,
            tratamentos: [],
          };

          if (tratamento?.tributo && tratamento?.regime) {
            const tratamentoJaAdicionado = fundamento.tratamentos.some(
              (item) =>
                item?.tributo?.codigo === tratamento.tributo.codigo && item?.regime?.codigo === tratamento.regime.codigo
            );

            if (!tratamentoJaAdicionado) {
              fundamento.tratamentos.push({
                chave: tratamentoDisponivel.chave,
                tributo: { ...tratamento.tributo },
                regime: { ...tratamento.regime },
              });
            }
          }

          fundamentosMap.set(fundamento.id, fundamento);
        });

        const fundamentosUnicos = [...fundamentosMap.values()];

        if (isMounted) setListFundamentos(fundamentosUnicos);
      } catch (error) {
        console.error('Erro ao buscar fundamentos legais opcionais.', error);
      }
    };

    fetchFundamentos();

    return () => {
      isMounted = false;
    };
  }, []);

  const tratamentosSelecionados = get(formValues, name, []);
  const codigosFundamentosLegaisSelecionados = useMemo(
    () =>
      (_.isArray(tratamentosSelecionados) ? tratamentosSelecionados : [])
        .map((tratamento) => tratamento?.fundamento?.id)
        .filter((codigoFundamentoLegal) => codigoFundamentoLegal !== null && codigoFundamentoLegal !== undefined)
        .map(String),
    [tratamentosSelecionados]
  );
  const codigosFundamentosLegaisSelecionadosKey = codigosFundamentosLegaisSelecionados.join(',');

  useEffect(() => {
    if (!faturaId) return undefined;

    let isMounted = true;

    const filtrarMercadorias = async () => {
      try {
        const response = await doPostQCXRequest(
          ['diduimp', 'auxiliar', 'filtrar-mercadorias-por-tratamento-tributario', faturaId],
          codigosFundamentosLegaisSelecionadosKey ? codigosFundamentosLegaisSelecionadosKey.split(',') : []
        );

        const mercadoriaIdsPermitidas = Array.isArray(response?.data)
          ? response.data
              .map((mercadoriaId) => Number(mercadoriaId))
              .filter((mercadoriaId) => Number.isInteger(mercadoriaId))
          : [];

        if (isMounted) {
          console.log('Mercadorias filtradas por tratamentos tributários:', response?.data);
          onMercadoriasPermitidasChange?.(mercadoriaIdsPermitidas);
        }
      } catch (error) {
        console.error('Erro ao filtrar mercadorias por tratamentos tributários.', error);
      }
    };

    filtrarMercadorias();

    return () => {
      isMounted = false;
    };
  }, [codigosFundamentosLegaisSelecionadosKey, faturaId, onMercadoriasPermitidasChange]);

  const tratamentosProps = useMemo(
    () => ({
      name,
      columns: [
        {
          field: 'tributo',
          headerName: 'Tributo',
          type: 'string',
          flex: 300,
          valueGetter: ({ row }) => `${row?.tributo?.codigo} - ${row?.tributo?.nome}`,
        },
        {
          field: 'regime',
          headerName: 'Regime Tributário',
          type: 'string',
          flex: 300,
          valueGetter: ({ row }) => `${row?.regime?.codigo} - ${row?.regime?.nome}`,
        },
        {
          field: 'fundamento',
          headerName: 'Fundamento Legal Opcional',
          type: 'string',
          flex: 300,
          valueGetter: ({ row }) => `${row?.fundamento?.id} - ${row?.fundamento?.nome}`,
        },
      ],
    }),
    [name]
  );

  const tratamentosFormProps = useMemo(
    () => ({
      rootName: 'ignorableFields.tratamentos',
      fields: [],
    }),
    []
  );

  const handleAlreadyExistsTratamento = useCallback((currentVinculo, currentVinculosList) => {
    const codigoFundamento = get(currentVinculo, 'selectFundamentosOpcionaisCode');

    return (
      _.isArray(currentVinculosList) &&
      currentVinculosList.some((tratamento) => tratamento?.fundamento?.id === codigoFundamento)
    );
  }, []);

  const handleAddTratamento = useCallback(
    (currentVinculo, currentVinculos) => {
      const codigoFundamento = get(currentVinculo, 'selectFundamentosOpcionaisCode');
      const fundamentoSelecionado = listFundamentos.find((fundamento) => fundamento.id === codigoFundamento);

      if (!fundamentoSelecionado) return;

      const fundamento = {
        id: fundamentoSelecionado.id,
        codigo: fundamentoSelecionado.tipo,
        nome: fundamentoSelecionado.nome,
      };
      const novosTratamentos = fundamentoSelecionado.tratamentos.map((tratamento) => ({
        id: uuid(),
        chave: tratamento.chave,
        codigo: fundamentoSelecionado.id,
        fundamento: { ...fundamento },
        regime: { ...tratamento.regime },
        tributo: { ...tratamento.tributo },
      }));

      form.change(name, [...(_.isArray(currentVinculos) ? currentVinculos : []), ...novosTratamentos]);
      form.change('ignorableFields.tratamentos.selectFundamentosOpcionaisCode', null);
    },
    [form, listFundamentos, name]
  );

  const handleRemoveTratamento = useCallback(
    ({ target, list }) => {
      const codigoFundamento = target?.fundamento?.id;

      if (!codigoFundamento || !_.isArray(list)) return;

      form.change(
        name,
        list.filter((tratamento) => tratamento?.fundamento?.id !== codigoFundamento)
      );
    },
    [form, name]
  );

  const handleUpdateTratamento = useCallback(() => {}, []);

  return (
    <QCXFinalBondManager
      isParentConsult={disabled}
      listProps={tratamentosProps}
      formProps={tratamentosFormProps}
      handleAdd={handleAddTratamento}
      handleRemove={handleRemoveTratamento}
      handleUpdate={handleUpdateTratamento}
      handleAlreadyExists={handleAlreadyExistsTratamento}
      disableDefaultRemove
      forceShowDelete
    >
      <Grid item xs={12} sm={12} md={7} lg={7}>
        <SelectFundamentosOpcionais
          listFundamentos={listFundamentos}
          name="ignorableFields.tratamentos.selectFundamentosOpcionaisCode"
          values={values}
          disabled={disabled}
          {...restProps}
        />
      </Grid>
    </QCXFinalBondManager>
  );
}
